import { EnvironmentInjector, inject, runInInjectionContext } from "@angular/core";
import { CanActivateFn, UrlTree } from "@angular/router";
import { from, Observable, of } from "rxjs";
import { concatMap, first } from "rxjs/operators";

/**
 * Composes multiple functional guards into a single guard that executes
 * them sequentially, short-circuiting on false or UrlTree.
 *
 * Angular's native canActivate array runs guards in parallel via
 * combineLatest (prioritizedGuardValue). This helper restores true
 * sequential execution for guards that depend on each other's side
 * effects (e.g. appAgentGuard must set the application before
 * authAgentGuard can authenticate).
 */
export function sequentialGuards(...guards: CanActivateFn[]): CanActivateFn {
  return (route, state) => {
    const envInjector = inject(EnvironmentInjector);

    return from(guards).pipe(
      concatMap((guard) => {
        const result = runInInjectionContext(envInjector, () =>
          guard(route, state)
        );
        if (result instanceof Observable) {
          return result;
        } else if (result instanceof Promise) {
          return from(result);
        } else {
          return of(result);
        }
      }),
      first(
        (result): result is false | UrlTree => result !== true,
        true as boolean | UrlTree
      )
    );
  };
}
